import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '@/lib/cache'
import { toCamelCase, toSnakeCase } from '@/lib/typeHelper'
import {
  type PolicyChangeAskKind,
  type PolicyChangeQuote,
  type PolicyChangeRequest,
  type PolicyChangeRow,
  PolicyChangeStatus,
} from '@/models/policyChange'

// The console's side of a customer's policy change request, which staff call an endorsement.

export enum EndorsementQueueView {
  NEEDS_ACTION = 'needs_action',
  WAITING_ON_CUSTOMER = 'waiting_on_customer',
  WITH_CARRIER = 'with_carrier',
  OPEN = 'open',
  CLOSED = 'closed',
}

/** The steps the API lets staff take on a request now. */
export enum EndorsementStaffAction {
  SEND_TO_CARRIERS = 'send_to_carriers',
  /** Ask the customer to change the request; it goes back to them. */
  ASK = 'ask',
  /** Ask the customer to reply or send a document; the request stays on its step. */
  ASK_REPLY = 'ask_reply',
  EDIT_QUOTES = 'edit_quotes',
  POST_QUOTES = 'post_quotes',
  APPROVE_UNCHANGED = 'approve_unchanged',
  REQUOTE = 'requote',
  MARK_BOUND = 'mark_bound',
  MARK_POLICY_UPDATED = 'mark_policy_updated',
  COMPLETE = 'complete',
  DROP = 'drop',
  DECLINE = 'decline',
}

interface OrgRef {
  id: string
  name: string
}

export interface InternalEndorsementRow extends PolicyChangeRow {
  org: OrgRef
}

/** What happened to one Corgi policy when the request was sent to the carriers; on that step's response only. */
export interface CorgiSendOutcome {
  policyNumber: string
  added: string[]
  leftForStaff: string[]
  /** Why no draft was made at all, such as a disconnected portal; empty when drafts were made. */
  error: string
}

export interface InternalEndorsement extends Omit<PolicyChangeRequest, 'actions'> {
  corgiSend?: CorgiSendOutcome[]
  org: OrgRef
  signature: string
  actions: EndorsementStaffAction[]
}

/** What staff record for one policy's quote; amounts travel as decimal strings. */
export type QuoteInput = Partial<
  Pick<
    PolicyChangeQuote,
    'carrierReference' | 'proratedChange' | 'annualChange' | 'newTotalPremium' | 'effectiveDate' | 'expiresOn'
  >
>

const BASE = '/api/v1/insure/internal/endorsements'

const asEndorsement = (data: unknown) => toCamelCase(data) as InternalEndorsement

export function useInternalEndorsements(view: EndorsementQueueView) {
  const { data, isLoading, isError } = useQuery({
    queryKey: [CacheKeys.INTERNAL_ENDORSEMENTS, 'list', view],
    queryFn: () =>
      axios
        .get(`${BASE}/`, { params: { view } })
        .then((r) => toCamelCase(r.data) as InternalEndorsementRow[]),
  })
  return { rows: data ?? [], isLoading, isError }
}

export function useInternalEndorsement(requestId: string | undefined) {
  return useQuery({
    queryKey: [CacheKeys.INTERNAL_ENDORSEMENTS, requestId],
    queryFn: () => axios.get(`${BASE}/${requestId}/`).then((r) => asEndorsement(r.data)),
    enabled: !!requestId,
    refetchInterval: (query) => (corgiIsPricing(query.state.data) ? CORGI_PRICING_POLL_MS : false),
  })
}

/** Corgi prices a submitted draft within seconds and Cai reads it back in the background; poll until it shows. */
export const CORGI_PRICING_POLL_MS = 10_000

export function corgiIsPricing(endorsement: InternalEndorsement | undefined): boolean {
  // A closed request keeps its last read of Corgi, so only one still with the carriers has a price to wait for.
  if (endorsement?.status !== PolicyChangeStatus.WITH_CARRIER) return false
  return endorsement.quotes.some((quote) =>
    quote.corgiDraft.endorsements.some((corgi) => corgi.status === 'quoting')
  )
}

// Shared by every step, so a page can hold its other steps while one is in flight.
export const ENDORSEMENT_STEP_KEY = [CacheKeys.INTERNAL_ENDORSEMENTS, 'step']

/** Every step returns the whole request: it replaces the cached one and refreshes the queue. */
export function useEndorsementStep<T>(send: (input: T) => Promise<{ data: unknown }>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ENDORSEMENT_STEP_KEY,
    mutationFn: (input: T) => send(input).then((r) => asEndorsement(r.data)),
    onSuccess: async (endorsement) => {
      // A read already in flight could finish after this step and put back what it replaced.
      await queryClient.cancelQueries({ queryKey: [CacheKeys.INTERNAL_ENDORSEMENTS, endorsement.id] })
      queryClient.setQueryData([CacheKeys.INTERNAL_ENDORSEMENTS, endorsement.id], endorsement)
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.INTERNAL_ENDORSEMENTS, 'list'] })
      // A decline, drop or bind moves the customer's pending roster rows too.
      for (const roster of [CacheKeys.TRUCKS, CacheKeys.TRAILERS, CacheKeys.DRIVERS]) {
        void queryClient.invalidateQueries({ queryKey: [roster, endorsement.org.id] })
      }
    },
  })
}

export const endorsementSteps = {
  sendToCarriers: (id: string) => axios.post(`${BASE}/${id}/send-to-carriers/`),
  ask: ({
    id,
    question,
    kind,
    needsDocument,
  }: {
    id: string
    question: string
    kind: PolicyChangeAskKind
    needsDocument: boolean
  }) => axios.post(`${BASE}/${id}/ask/`, { question, kind, needs_document: needsDocument }),
  decline: ({ id, reason }: { id: string; reason: string }) =>
    axios.post(`${BASE}/${id}/decline/`, { reason }),
  dropChange: ({ id, changeId, reason }: { id: string; changeId: string; reason: string }) =>
    axios.post(`${BASE}/${id}/changes/${changeId}/drop/`, { reason }),
  updateQuote: ({ id, quoteId, quote }: { id: string; quoteId: string; quote: QuoteInput }) =>
    axios.patch(`${BASE}/${id}/quotes/${quoteId}/`, toSnakeCase(quote)),
  buildCorgiDraft: ({ id, quoteId }: { id: string; quoteId: string }) =>
    axios.post(`${BASE}/${id}/quotes/${quoteId}/corgi-draft/`),
  submitCorgiQuote: ({ id, quoteId }: { id: string; quoteId: string }) =>
    axios.post(`${BASE}/${id}/quotes/${quoteId}/corgi-submit/`),
  syncCorgiQuote: ({ id, quoteId }: { id: string; quoteId: string }) =>
    axios.post(`${BASE}/${id}/quotes/${quoteId}/corgi-sync/`),
  postQuotes: (id: string) => axios.post(`${BASE}/${id}/post-quotes/`),
  approveUnchanged: (id: string) => axios.post(`${BASE}/${id}/approve-unchanged/`),
  requote: (id: string) => axios.post(`${BASE}/${id}/requote/`),
  markBound: (id: string) => axios.post(`${BASE}/${id}/mark-bound/`),
  markPolicyUpdated: (id: string) => axios.post(`${BASE}/${id}/mark-policy-updated/`),
  complete: (id: string) => axios.post(`${BASE}/${id}/complete/`),
}
